import {SERIES_COLORS, MONTH_COUNT} from '../../consts'
import {MonthlyHierarchyAnalysisDataPoint, ValueDisplayMode, HierarchyEntry} from '../../types'

import {getAnswered, getTotal} from './difficultyScore.utils'

const computeValue = (answeredPositively: number, questionCount: number, mode: ValueDisplayMode) =>
  mode === 'percentage'
    ? Math.round((answeredPositively / questionCount) * 100)
    : answeredPositively

const getValueFromPoint = (
  point: MonthlyHierarchyAnalysisDataPoint,
  mode: ValueDisplayMode
): number | null => {
  const scores = point.difficultyScores
  if (scores) {
    const answered =
      getAnswered(scores.basic) + getAnswered(scores.advanced) + getAnswered(scores.excellence)
    const total =
      getTotal(scores.basic) + getTotal(scores.advanced) + getTotal(scores.excellence)
    return total > 0 ? computeValue(answered, total, mode) : answered
  }
  const totalScore = point.totalScore
  if (totalScore && totalScore.questionCount > 0) {
    return computeValue(totalScore.answeredPositively, totalScore.questionCount, mode)
  }
  return totalScore?.answeredPositively ?? null
}

/**
 * Builds monthly series data.
 * When `year` is provided, a 13-item array is returned:
 *   index 0 = December of (year-1), indices 1-12 = Jan-Dec of year.
 * Without `year`, a 12-item array by month index is returned (legacy).
 */
export const buildSeriesData = (
  hierarchyEntries: HierarchyEntry[],
  monthlyData: MonthlyHierarchyAnalysisDataPoint[],
  mode: ValueDisplayMode = 'total',
  year?: number
) =>
  hierarchyEntries.map((entry, index) => {
    const monthlyNodeData = monthlyData.filter((dp) => dp.hierarchyId === entry.id)

    let seriesData: (number | null)[]
    if (year !== undefined) {
      // Map by "YYYY-MM" for precise lookup
      const dataByMonthStr = new Map(
        monthlyNodeData.map((dp) => [dp.month.substring(0, 7), dp])
      )
      // Position 0: Dec of previous year
      const prevDecStr = `${year - 1}-12`
      const prevDecPoint = dataByMonthStr.get(prevDecStr)
      const prevDecValue = prevDecPoint ? getValueFromPoint(prevDecPoint, mode) : null

      // Positions 1-12: Jan-Dec of year
      const regularData = Array.from({length: MONTH_COUNT}, (_, i) => {
        const monthStr = `${year}-${String(i + 1).padStart(2, '0')}`
        const point = dataByMonthStr.get(monthStr)
        return point ? getValueFromPoint(point, mode) : null
      })

      seriesData = [prevDecValue, ...regularData]
    } else {
      const dataByMonth = new Map(monthlyNodeData.map((dp) => [new Date(dp.month).getMonth(), dp]))
      seriesData = Array.from({length: MONTH_COUNT}, (_, monthIndex) => {
        const point = dataByMonth.get(monthIndex)
        return point ? getValueFromPoint(point, mode) : null
      })
    }

    const color = SERIES_COLORS[index % SERIES_COLORS.length]

    return {
      name: entry.name,
      type: 'line' as const,
      data: seriesData,
      smooth: true,
      emphasis: {focus: 'none' as const},
      zlevel: 1,
      lineStyle: {color},
      itemStyle: {color}
    }
  })
